import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, getDoc, collection, getDocs, writeBatch, increment, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { FiCalendar, FiMapPin, FiUser, FiArrowLeft, FiX, FiCheck, FiTrash2, FiUserCheck, FiChevronRight, FiMinus, FiPlus } from 'react-icons/fi';

// --- STÍLUSOK ---
const pageContainerStyle = {
    padding: '2rem',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f0f2f5',
    minHeight: 'calc(100vh - 4rem)',
    maxWidth: '800px',
    margin: '0 auto'
};

const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    position: 'relative'
};

const titleStyle = {
    color: '#b30000',
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    paddingRight: '120px' // Hely az ikonnak
};

const modalOverlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center',
    alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(3px)'
};

const modalContentStyle = {
    backgroundColor: '#fff', width: '90%', maxWidth: '400px', borderRadius: '15px',
    boxShadow: '0 15px 35px rgba(0,0,0,0.3)', overflow: 'hidden', textAlign: 'center'
};

const modalHeaderStyle = {
    backgroundColor: '#b30000', color: '#fff', padding: '15px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
};

const modalBodyStyle = { padding: '25px' };

const withdrawButtonStyle = {
    width: '100%', backgroundColor: '#b30000', color: '#fff', border: 'none',
    padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '15px'
};

const cancelButtonStyle = {
    width: '100%', backgroundColor: '#eee', color: '#333', border: 'none',
    padding: '12px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '10px'
};

const detailStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#555',
    marginBottom: '0.5rem',
    fontSize: '1rem'
};

const tabContainerStyle = {
    display: 'flex',
    gap: '20px',
    borderBottom: '2px solid #eee',
    marginBottom: '1.5rem',
    marginTop: '2rem'
};

const tabButtonStyle = (isActive) => ({
    padding: '10px 20px',
    border: 'none',
    background: 'none',
    fontSize: '1rem',
    fontWeight: 'bold',
    color: isActive ? '#b30000' : '#666',
    borderBottom: isActive ? '3px solid #b30000' : '3px solid transparent',
    cursor: 'pointer',
    marginBottom: '-2px',
    transition: 'all 0.2s'
});

const descriptionStyle = {
    whiteSpace: 'pre-wrap',
    lineHeight: '1.6',
    color: '#444'
};

const participantHeaderStyle = {
    backgroundColor: '#fff5f5',
    padding: '8px 15px',
    borderRadius: '8px',
    color: '#b30000',
    fontWeight: 'bold',
    fontSize: '0.9rem',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    borderLeft: '4px solid #b30000'
};

const participantRowStyle = {
    padding: '10px 15px',
    borderBottom: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column'
};

const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    marginBottom: '1rem',
    fontSize: '1rem',
    padding: 0
};

const profileRegCardStyle = {
    backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '10px',
    border: '1px solid #eee', marginBottom: '15px'
};

const genderToggleStyle = {
    backgroundColor: '#b30000', color: '#fff', border: 'none', padding: '6px 12px',
    borderRadius: '8px', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '5px'
};

const inputGroupStyle = { marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '5px' };

const regLabelStyle = { fontSize: '0.75rem', fontWeight: 'bold', color: '#b30000', textTransform: 'uppercase' };

const regSelectStyle = {
    padding: '10px', borderRadius: '8px', border: '1px solid #ddd',
    backgroundColor: '#fff', fontSize: '0.9rem'
};

const regTextAreaStyle = {
    ...regSelectStyle, minHeight: '80px', resize: 'vertical'
};

const lunchCounterStyle = {
    display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#f8f9fa',
    padding: '5px', borderRadius: '8px', border: '1px solid #ddd', width: 'fit-content'
};

const lunchBtnStyle = {
    backgroundColor: '#fff', color: '#b30000', border: '1px solid #ddd',
    width: '32px', height: '32px', borderRadius: '6px', cursor: 'pointer',
    display: 'flex', justifyContent: 'center', alignItems: 'center'
};

const regSubmitButtonStyle = {
    width: '100%', backgroundColor: '#b30000', color: '#fff', border: 'none',
    padding: '14px', borderRadius: '10px', fontWeight: 'bold', fontSize: '1rem',
    cursor: 'pointer', marginTop: '10px', boxShadow: '0 4px 6px rgba(179,0,0,0.2)'
};

// Fordítási táblázat
const translationMap = {
    'mini': 'Mini', 'gyerek': 'Gyerek', 'junior': 'Junior', 'kadet': 'Kadét',
    'fiatal_felnott': 'Fiatal felnőtt', 'felnott': 'Felnőtt', 'szenior': 'Szenior', 'veteran': 'Veterán',
    '3d_hdh_bb': 'BB (Barebow)', '3d_hdh_bg': 'BG (Fúvócső)', '3d_hdh_cb': 'CB (Csigás Barebow)',
    '3d_hdh_crb': 'CRB (Számszeríj)', '3d_hdh_cu': 'CU (Scope-os Csigás)',
    '3d_hdh_hu': 'HU (Vadász Csigás)', '3d_hdh_ol': 'OL (Olimpiai)',
    '3d_hdh_pbhb': 'PBHB (Tradi)', '3d_hdh_trlb': 'TRLB (Longbow)',
    '3d_hdh_trrb': 'TRRB (Vadászreflex)',
    'terep_olimpiai': 'OL (Olimpiai)', 'terep_csigas': 'CU (Scope-os Csigás)',
    'terep_barebow': 'BB (Barebow)', 'terep_cb': 'CB (Csigás Barebow)',
    'terep_hu': 'HU (Vadász Csigás)', 'terep_pbhb': 'PBHB (Tradi)',
    'terep_trlb': 'TRLB (Longbow)', 'terep_trrb': 'TRRB (Vadászreflex)',
    'palya_wa_ol': 'R (Olimpiai reflexíj)', 'palya_wa_cu': 'CU (Scope-os Csigás)',
    'palya_wa_hu': 'HU (Vadász Csigás)', 'palya_wa_bb': 'B (Barebow)',
    'palya_wa_trrb': 'TRRB (Vadászreflex)', 'palya_wa_trlb': 'TRLB (Longbow)',
    'palya_wa_pbhb': 'PBHB (Tradi)', 'palya_wa_crb': 'CRB (Számszeríj)',
    'tortenelmi_pusztai_modern': 'Modern pusztai íj',
    'tortenelmi_pusztai_termeszetes': 'Természetes pusztai íj',
    'tortenelmi_botij': 'Történelmi hosszúíj / Botíj',
    'tortenelmi_vadaszij': 'Hagyományos vadászíj',
    'tortenelmi_szamszerij': 'Történelmi számszeríj',
    'tortenelmi_bb': 'BB (Barebow)',
    'orom_wa_ol': 'OL (Olimpiai reflexíj)', 'orom_wa_cu': 'CU (Scope-os Csigás)',
    'orom_wa_hu': 'HU (Vadász Csigás)', 'orom_wa_cb': 'CB (Csigás Barebow)',
    'orom_wa_bb': 'BB (Barebow)', 'orom_wa_trrb': 'TRRB (Vadászreflex)',
    'orom_wa_trlb': 'TRLB (Longbow)', 'orom_wa_pbhb': 'PBHB (Pusztai íj)',
    'orom_wa_crb': 'CRB (Számszeríj)', 'orom_wa_bg': 'BG (Fúvócső)',
    'egyeb_wa_ol': 'OL (Olimpiai reflexíj)', 'egyeb_wa_cu': 'CU (Scope-os Csigás)',
    'egyeb_wa_hu': 'HU (Vadász Csigás)', 'egyeb_wa_cb': 'CB (Csigás Barebow)',
    'egyeb_wa_bb': 'BB (Barebow)', 'egyeb_wa_trrb': 'TRRB (Vadászreflex)',
    'egyeb_wa_trlb': 'TRLB (Longbow)', 'egyeb_wa_pbhb': 'PBHB (Pusztai íj)',
    'egyeb_wa_crb': 'CRB (Számszeríj)', 'egyeb_wa_bg': 'BG (Fúvócső)',
    'egyeb_altalanos': 'Egyéb Általános'
};

const getCompetitionTypeDisplay = (rawType) => {
    const type = rawType?.toLowerCase() || '';
    if (type.includes('3d')) return { label: '3D', color: '#28a745' };
    if (type.includes('palya') || type.includes('pálya')) return { label: 'Pálya', color: '#007bff' };
    if (type.includes('orom') || type.includes('öröm')) return { label: 'Örömijász', color: '#ffc107' };
    if (type.includes('tortenelmi') || type.includes('történelmi')) return { label: 'Történelmi', color: '#dc3545' };
    if (type.includes('terep')) return { label: 'Terep', color: '#6d4c41' };
    if (type.includes('egyeb') || type.includes('egyéb')) return { label: 'Egyéb', color: '#9c27b0' };
    return { label: rawType || 'Egyéb', color: '#9c27b0' };
};

const FullViewCompetition = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [competition, setCompetition] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingParticipants, setLoadingParticipants] = useState(false);
    const [activeTab, setActiveTab] = useState('description');
    const [selectedEvent, setSelectedEvent] = useState(null); // ÚJ
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null); // ÚJ
    const [isJoined, setIsJoined] = useState(false);

    // --- NEVEZÉSI MODAL ÁLLAPOTOK ---
    const [showRegModal, setShowRegModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [regData, setRegData] = useState({
        gender: 'Férfi',
        bowCategory: '',
        ageCategory: '',
        lunchCount: 0,
        comment: ''
    });

    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [withdrawSuccess, setWithdrawSuccess] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);

        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user && id) {
                // Profil adatok betöltése
                try {
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const profile = userDocSnap.data();
                        setUserProfile(profile);
                        setRegData(prev => ({ ...prev, gender: profile.gender || 'Férfi' }));
                    }
                } catch (err) { console.error(err); }

                const partRef = doc(db, "events", id, "participants", user.uid);
                const partSnap = await getDoc(partRef);
                setIsJoined(partSnap.exists());
            } else {
                setIsJoined(false);
                setUserProfile(null);
            }
        });
        return () => {
            unsubscribeAuth();
            window.removeEventListener('resize', handleResize);
        };
    }, [id]);

    const handleJoinSubmit = async () => {
        if (!regData.bowCategory || !regData.ageCategory) {
            alert("Kérjük, válassz kategóriát és korosztályt!");
            return;
        }

        setIsSubmitting(true);
        try {
            const batch = writeBatch(db);
            const participantRef = doc(db, "events", id, "participants", currentUser.uid);

            // iOS detektálás
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            const platform = isIOS ? "ios" : "web";

            const registrationData = {
                userId: currentUser.uid,
                lastName: userProfile?.lastName || '',
                firstName: userProfile?.firstName || '',
                fullName: `${userProfile?.lastName || ''} ${userProfile?.firstName || ''}`.trim(),
                club: userProfile?.clubName || userProfile?.club || '',
                gender: regData.gender,
                birthDate: userProfile?.birthDate || 0,
                bowCategory: regData.bowCategory,
                ageCategory: regData.ageCategory,
                entryFee: competition.ageFees?.[regData.ageCategory] || '',
                lunchCount: regData.lunchCount,
                comment: regData.comment,
                registrationDate: serverTimestamp(),
                status: "Nevezve",
                registeredVia: platform
            };

            batch.set(participantRef, registrationData);

            const eventRef = doc(db, "events", id);
            batch.update(eventRef, {
                participantsCount: increment(1)
            });

            await batch.commit();
            setIsJoined(true);
            setRegistrationSuccess(true);

            setTimeout(() => {
                setShowRegModal(false);
                setRegistrationSuccess(false);
                // Frissítjük a résztvevők listáját ha a tabon vagyunk
                if (activeTab === 'participants') {
                    setParticipants(prev => [...prev, registrationData]);
                }
            }, 2000);

        } catch (err) {
            console.error("Hiba a nevezés mentésekor:", err);
            alert("Hiba történt a mentés során.");
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const fetchCompetition = async () => {
            try {
                const docRef = doc(db, "events", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setCompetition({ id: docSnap.id, ...docSnap.data() });
                } else {
                    alert("A verseny nem található!");
                    navigate('/versenyek');
                }
            } catch (error) {
                console.error("Hiba a verseny betöltésekor:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchCompetition();
    }, [id, navigate]);

    useEffect(() => {
        if (activeTab === 'participants' && participants.length === 0) {
            const fetchParticipants = async () => {
                setLoadingParticipants(true);
                try {
                    const partsRef = collection(db, "events", id, "participants");
                    const querySnapshot = await getDocs(partsRef);
                    const partsData = querySnapshot.docs.map(doc => doc.data());
                    setParticipants(partsData);
                } catch (error) {
                    console.error("Hiba a nevezők betöltésekor:", error);
                } finally {
                    setLoadingParticipants(false);
                }
            };
            fetchParticipants();
        }
    }, [activeTab, id, participants.length]);

    const groupedParticipants = useMemo(() => {
        const groups = {};
        participants.forEach(p => {
            const key = `${p.bowCategory}_${p.ageCategory}_${p.gender}`;
            if (!groups[key]) {
                groups[key] = {
                    bowCategory: p.bowCategory,
                    ageCategory: p.ageCategory,
                    gender: p.gender,
                    members: []
                };
            }
            groups[key].members.push(p);
        });

        return Object.values(groups).sort((a, b) => {
            const nameA = `${translationMap[a.bowCategory] || a.bowCategory} ${translationMap[a.ageCategory] || a.ageCategory}`;
            const nameB = `${translationMap[b.bowCategory] || b.bowCategory} ${translationMap[b.ageCategory] || b.ageCategory}`;
            return nameA.localeCompare(nameB);
        });
    }, [participants]);

    const handleWithdrawSubmit = async () => {
        if (!currentUser || !id) return;
        setIsWithdrawing(true);
        try {
            const batch = writeBatch(db);
            const participantRef = doc(db, "events", id, "participants", currentUser.uid);
            const eventRef = doc(db, "events", id);

            batch.delete(participantRef);
            batch.update(eventRef, {
                participantsCount: increment(-1)
            });

            await batch.commit();
            setIsJoined(false);
            setWithdrawSuccess(true);

            // Frissítjük a résztvevők listáját ha a tabon vagyunk
            if (activeTab === 'participants') {
                setParticipants(prev => prev.filter(p => p.userId !== currentUser.uid));
            }

            // 2 másodperc múlva bezárjuk az ablakot
            setTimeout(() => {
                setShowWithdrawModal(false);
                setWithdrawSuccess(false);
            }, 2000);

        } catch (error) {
            console.error("Hiba a visszavonáskor:", error);
            alert("Hiba történt a művelet során.");
        } finally {
            setIsWithdrawing(false);
        }
    };

    if (loading) {
        return <div style={pageContainerStyle}><p style={{textAlign: 'center'}}>Betöltés... 🏹</p></div>;
    }

    if (!competition) return null;

    const typeDisplay = getCompetitionTypeDisplay(competition.competitionType);

    return (
        <div className="force-light-theme" style={{ ...pageContainerStyle, padding: isMobile ? '0' : '2rem', maxWidth: isMobile ? '100%' : '800px' }}>
            {/* --- NEVEZÉSI MODAL (ARCHERY NEXUS STÍLUS) --- */}
            {showRegModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={modalHeaderStyle}>
                            <h3>Nevezés a versenyre</h3>
                            <div
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: '#f2f2f2',
                                    borderRadius: '50%',
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => setShowRegModal(false)}
                            >
                                <FiX size={20} color="#333" />
                            </div>
                        </div>

                        <div style={modalBodyStyle}>
                            {registrationSuccess ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '40px 0',
                                    gap: '15px'
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#e6fffa',
                                        color: '#28a745',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '2.5rem',
                                        boxShadow: '0 4px 10px rgba(40, 167, 69, 0.2)'
                                    }}>
                                        <FiCheck />
                                    </div>
                                    <h2 style={{ color: '#28a745', margin: 0 }}>Sikeres nevezés!</h2>
                                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Sok sikert a versenyon!</p>
                                </div>
                            ) : (
                                <>
                                    <p style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem', marginBottom: '20px' }}>
                                        Kérjük, ellenőrizze adatait
                                    </p>

                                    {/* PROFIL KÁRTYA */}
                                    <div style={profileRegCardStyle}>
                                        <div style={{ display: 'flex', gap: '5px', fontSize: '0.9rem', marginBottom: '5px' }}>
                                            <strong>NÉV:</strong>
                                            <span>{userProfile?.lastName} {userProfile?.firstName}</span>
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>
                                            {userProfile?.clubName || userProfile?.club || 'Nincs egyesület'}
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <button
                                                style={genderToggleStyle}
                                                onClick={() => setRegData(prev => ({ ...prev, gender: prev.gender === 'Férfi' ? 'Nő' : 'Férfi' }))}
                                            >
                                                {regData.gender === 'Férfi' ? 'Férfi' : 'Nő'} <FiChevronRight />
                                            </button>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#888' }}>SZÜLETÉSI DÁTUM</div>
                                                <div style={{ fontSize: '0.9rem' }}>
                                                    {userProfile?.birthDate ? new Date(userProfile.birthDate).toLocaleDateString('hu-HU') : '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '20px 0' }} />

                                    {/* ÍJKATEGÓRIA VÁLASZTÓ */}
                                    <div style={inputGroupStyle}>
                                        <label style={regLabelStyle}>Válassz íjkategóriát</label>
                                        <select
                                            style={regSelectStyle}
                                            value={regData.bowCategory}
                                            onChange={(e) => setRegData(prev => ({ ...prev, bowCategory: e.target.value }))}
                                        >
                                            <option value="">Válassz kategóriát...</option>
                                            {competition?.categories?.map(cat => (
                                                <option key={cat} value={cat}>{translationMap[cat] || cat}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* KOROSZTÁLY VÁLASZTÓ */}
                                    <div style={inputGroupStyle}>
                                        <label style={regLabelStyle}>Válassz korosztályt</label>
                                        <select
                                            style={regSelectStyle}
                                            value={regData.ageCategory}
                                            onChange={(e) => setRegData(prev => ({ ...prev, ageCategory: e.target.value }))}
                                        >
                                            <option value="">Válassz korosztályt...</option>
                                            {competition?.ageCategories?.map(age => (
                                                <option key={age} value={age}>{translationMap[age] || age}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* EBÉD (HA ELÉRHETŐ) */}
                                    {competition?.lunchAvailable && (
                                        <div style={inputGroupStyle}>
                                            <label style={regLabelStyle}>Ebéd igény (adag):</label>
                                            <div style={lunchCounterStyle}>
                                                <button
                                                    style={lunchBtnStyle}
                                                    onClick={() => setRegData(prev => ({ ...prev, lunchCount: Math.max(0, prev.lunchCount - 1) }))}
                                                >
                                                    <FiMinus />
                                                </button>
                                                <span style={{ width: '40px', textAlign: 'center', fontWeight: 'bold' }}>{regData.lunchCount}</span>
                                                <button
                                                    style={lunchBtnStyle}
                                                    onClick={() => setRegData(prev => ({ ...prev, lunchCount: prev.lunchCount + 1 }))}
                                                >
                                                    <FiPlus />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* MEGJEGYZÉS */}
                                    <div style={inputGroupStyle}>
                                        <label style={regLabelStyle}>Megjegyzés a szervezőnek:</label>
                                        <textarea
                                            style={regTextAreaStyle}
                                            placeholder="Pl. csapattárs kérés..."
                                            value={regData.comment}
                                            onChange={(e) => setRegData(prev => ({ ...prev, comment: e.target.value }))}
                                        />
                                    </div>

                                    <button
                                        style={regSubmitButtonStyle}
                                        onClick={handleJoinSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Mentés...' : 'Nevezés beküldése'}
                                    </button>

                                    <p style={{ fontSize: '0.65rem', color: '#888', textAlign: 'center', marginTop: '15px', lineHeight: '1.4' }}>
                                        *A "Nevezés beküldése" gombra kattintva kijelented, hogy elolvastad és elfogadtad a versenyszabályzatot!
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {showWithdrawModal && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={modalHeaderStyle}>
                            <h3 style={{ margin: 0 }}>{withdrawSuccess ? 'Sikeres visszavonás' : 'Visszavonod a nevezésed?'}</h3>
                        </div>
                        <div style={modalBodyStyle}>
                            {withdrawSuccess ? (
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '20px 0',
                                    gap: '15px'
                                }}>
                                    <div style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#e6fffa',
                                        color: '#28a745',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '2.5rem',
                                        boxShadow: '0 4px 10px rgba(40, 167, 69, 0.2)'
                                    }}>
                                        <FiCheck />
                                    </div>
                                    <p style={{ color: '#666', fontSize: '0.9rem' }}>Nevezésed sikeresen törölve.</p>
                                </div>
                            ) : (
                                <>
                                    <FiTrash2 size={50} color="#b30000" style={{ marginBottom: '15px' }} />
                                    <p style={{ color: '#666', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                        Biztosan törölni szeretnéd a nevezésedet erről a versenyről?
                                    </p>
                                    <button
                                        style={withdrawButtonStyle}
                                        onClick={handleWithdrawSubmit}
                                        disabled={isWithdrawing}
                                    >
                                        {isWithdrawing ? 'Törlés...' : 'Igen, visszavonom'}
                                    </button>
                                    <button
                                        style={cancelButtonStyle}
                                        onClick={() => setShowWithdrawModal(false)}
                                        disabled={isWithdrawing}
                                    >
                                        Mégse
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!isMobile && (
                <button style={backButtonStyle} onClick={() => navigate('/versenyek')}>
                    <FiArrowLeft /> Vissza a versenyekhez
                </button>
            )}

            <div style={{
                ...cardStyle,
                padding: isMobile ? '20px 16px 100px 16px' : '2rem',
                borderRadius: isMobile ? '0' : '12px',
                boxShadow: isMobile ? 'none' : cardStyle.boxShadow
            }}>
                {/* ASZTALI NÉZET: NEVEZVE INDIKÁTOR */}
                {isJoined && !isMobile && (
                    <div style={{
                        position: 'absolute', top: '2rem', right: '2rem',
                        display: 'flex', alignItems: 'center', gap: '15px'
                    }}>
                        <button
                            onClick={() => setShowWithdrawModal(true)}
                            style={{
                                background: 'none', border: 'none', color: '#b30000',
                                cursor: 'pointer', display: 'flex', alignItems: 'center'
                            }}
                            title="Nevezés visszavonása"
                        >
                            <FiTrash2 size={24} />
                        </button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#28a745', fontWeight: 'bold', fontSize: '0.9rem' }}>
                            <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                                <FiUserCheck size={32} />
                                <div style={{
                                    position: 'absolute', top: -2, right: -2,
                                    backgroundColor: '#b30000', color: 'white', borderRadius: '50%',
                                    width: '14px', height: '14px', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', fontSize: '10px'
                                }}>
                                    ✓
                                </div>
                            </div>
                            <span>NEVEZVE</span>
                        </div>
                    </div>
                )}

                {/* MOBIL FEJLÉC (Android stílus) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2px' : '0.5rem' }}>
                    <h1 style={{
                        ...titleStyle,
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        color: isMobile ? '#D32F2F' : titleStyle.color,
                        paddingRight: (isJoined && !isMobile) ? '120px' : '0'
                    }}>
                        {competition.competitionName}
                    </h1>
                    <div style={{
                        ...detailStyle,
                        color: typeDisplay.color,
                        opacity: isMobile ? 0.8 : 1,
                        fontWeight: 'bold',
                        fontSize: isMobile ? '0.9rem' : '1rem'
                    }}>
                        {typeDisplay.label}
                    </div>
                </div>

                <div style={{ ...detailStyle, marginTop: isMobile ? '8px' : '0.5rem', fontSize: isMobile ? '1.1rem' : '1rem' }}>
                    {!isMobile && <FiCalendar />}
                    {competition.competitionDate || competition.startDate}
                    {competition.endDate && ` - ${competition.endDate}`}
                </div>

                <div style={{ ...detailStyle, marginTop: '4px', color: isMobile ? '#D32F2F' : '#555' }}>
                    {!isMobile && <FiMapPin color="#b30000" />}
                    {competition.location}
                </div>

                <div style={{ ...detailStyle, marginTop: isMobile ? '16px' : '0.5rem' }}>
                    {!isMobile && <FiUser />}
                    Szervező: {competition.organizingClub || 'Nincs megadva'}
                </div>

                {/* ELVÁLASZTÓ VONAL (Mobilon) */}
                {isMobile && <div style={{ height: '1px', backgroundColor: '#CCCCCC', margin: '16px 0' }} />}

                {/* TAB GOMBOK (Android stílus) */}
                <div style={{
                    ...tabContainerStyle,
                    marginTop: isMobile ? '0' : '2rem',
                    gap: isMobile ? '0' : '20px',
                    borderBottom: isMobile ? 'none' : '2px solid #eee'
                }}>
                    <div
                        style={{
                            flex: isMobile ? 1 : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => setActiveTab('description')}
                    >
                        <button style={{
                            ...tabButtonStyle(activeTab === 'description'),
                            color: (activeTab === 'description') ? '#D32F2F' : '#666',
                            borderBottom: 'none',
                            padding: isMobile ? '8px' : '10px 20px',
                            fontSize: isMobile ? '13px' : '1rem'
                        }}>
                            Leírás
                        </button>
                        {isMobile && (
                            <div style={{
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#D32F2F',
                                visibility: activeTab === 'description' ? 'visible' : 'hidden'
                            }} />
                        )}
                    </div>

                    <div
                        style={{
                            flex: isMobile ? 1 : 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            cursor: 'pointer'
                        }}
                        onClick={() => setActiveTab('participants')}
                    >
                        <button style={{
                            ...tabButtonStyle(activeTab === 'participants'),
                            color: (activeTab === 'participants') ? '#D32F2F' : '#666',
                            borderBottom: 'none',
                            padding: isMobile ? '8px' : '10px 20px',
                            fontSize: isMobile ? '13px' : '1rem'
                        }}>
                            Nevezők ({competition.participantsCount || participants.length || 0})
                        </button>
                        {isMobile && (
                            <div style={{
                                width: '60px',
                                height: '3px',
                                backgroundColor: '#D32F2F',
                                visibility: activeTab === 'participants' ? 'visible' : 'hidden'
                            }} />
                        )}
                    </div>
                </div>

                {/* LEÍRÁS TARTALOM */}
                {activeTab === 'description' && (
                    <div style={{ ...descriptionStyle, padding: isMobile ? '0' : '0' }}>
                        {competition.competitionDescription || 'Nincs leírás megadva.'}
                    </div>
                )}

                {/* NEVEZŐK TARTALOM */}
                {activeTab === 'participants' && (
                    <div>
                        {loadingParticipants ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#666' }}>Nevezők betöltése... 🏹</p>
                        ) : participants.length === 0 ? (
                            <p style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Még nincs nevező erre a versenyre.</p>
                        ) : (
                            groupedParticipants.map((group, idx) => (
                                <div key={idx} style={{ marginBottom: isMobile ? '1rem' : '2rem' }}>
                                    <div style={{
                                        ...participantHeaderStyle,
                                        marginTop: isMobile ? '1rem' : '1.5rem',
                                        fontSize: isMobile ? '0.85rem' : '0.9rem'
                                    }}>
                                        {translationMap[group.bowCategory] || group.bowCategory} - {translationMap[group.ageCategory] || group.ageCategory} - {group.gender}
                                    </div>
                                    {group.members.sort((a,b) => a.fullName.localeCompare(b.fullName)).map((p, pIdx) => (
                                        <div key={pIdx} style={{
                                            ...participantRowStyle,
                                            paddingVertical: isMobile ? '10px' : '10px',
                                            paddingHorizontal: isMobile ? '16px' : '15px'
                                        }}>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: '#333',
                                                fontSize: isMobile ? '16px' : '1rem'
                                            }}>
                                                {p.fullName}
                                            </span>
                                            <span style={{
                                                fontSize: isMobile ? '12px' : '0.75rem',
                                                color: '#666',
                                                marginTop: isMobile ? '1px' : '0'
                                            }}>
                                                {p.club || 'Egyéni induló'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* VISSZA GOMB (Mobilon alul a listában) */}
                {isMobile && (
                    <button
                        style={{
                            width: '100%',
                            background: 'none',
                            border: 'none',
                            color: '#666',
                            padding: '16px',
                            fontSize: '1rem',
                            marginTop: '8px'
                        }}
                        onClick={() => navigate('/versenyek')}
                    >
                        Vissza
                    </button>
                )}
            </div>

            {/* MOBIL FIX ALSÓ GOMBOK (Android stílus) */}
            {isMobile && (
                <div style={{
                    position: 'fixed',
                    bottom: '0',
                    left: 0,
                    right: 0,
                    padding: '16px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    display: 'flex',
                    gap: '8px',
                    zIndex: 100
                }}>
                    <button
                        style={{
                            flex: 1,
                            backgroundColor: isJoined ? '#ccc' : '#D32F2F',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '12px',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                        disabled={isJoined}
                        onClick={() => {
                            if (!currentUser) {
                                alert("A nevezéshez kérjük, jelentkezzen be!");
                                return;
                            }
                            setSelectedEvent(competition);
                            setRegistrationSuccess(false);
                            setRegData(prev => ({
                                ...prev,
                                bowCategory: '',
                                ageCategory: '',
                                lunchCount: 0,
                                comment: ''
                            }));
                            setShowRegModal(true);
                        }}
                    >
                        {isJoined ? "MÁR NEVEZTÉL" : "NEVEZÉS"}
                    </button>

                    {isJoined && (
                        <button
                            onClick={() => setShowWithdrawModal(true)}
                            style={{
                                width: '60px',
                                backgroundColor: '#666666',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }}
                        >
                            <FiTrash2 size={24} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FullViewCompetition;



